import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FitnessPlanService } from '../fitness-plan.service';
import { EditInstructionDialogComponent } from './edit-instruction-dialog/edit-instruction-dialog.component';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';

interface ExerciseInstruction {
  id?: string;
  memberId: string;
  exerciseId: string;
  exerciseName: string;
  customInstructions: string;
  createdAt?: string;
  updatedAt?: string;
  lastAssignedDate?: string;
  totalAssignments?: number;
  // Additional fields from the new API response
  instructions?: string[];
  benefits?: string[];
  safetyTips?: string[];
  muscleGroup?: string;
  difficultyLevel?: string;
  equipmentNeeded?: string[];
  estimatedCaloriesBurned?: number;
  calorieUnit?: string;
  diabeticGuidance?: string;
  riskAssessment?: string;
}

interface MemberExerciseDetailsResponse {
  memberName: string;
  memberExerciseDetails: {
    exerciseId: string;
    exerciseName: string;
    instructions: string[];
    benefits: string[];
    safetyTips: string[];
    muscleGroup: string;
    difficultyLevel: string;
    equipmentNeeded: string[];
    estimatedCaloriesBurned: number;
    calorieUnit: string;
    diabeticGuidance: string;
    riskAssessment: string;
  }[];
  count: number;
  message: string;
}

interface MemberExerciseSummary {
  memberId: string;
  memberName: string;
  exercises: ExerciseInstruction[];
}

@Component({
  selector: 'app-exercise-instructions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatCardModule,
    MatDialogModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './exercise-instructions.component.html',
  styleUrl: './exercise-instructions.component.css',
})
export class ExerciseInstructionsComponent implements OnInit {
  memberId: string = '';
  memberName: string = '';
  exercises: ExerciseInstruction[] = [];
  filteredExercises: ExerciseInstruction[] = [];
  searchTerm: string = '';
  isLoading: boolean = false;
  error: string = '';

  private expandedSections = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fitnessPlanService: FitnessPlanService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.memberId = this.route.snapshot.queryParamMap.get('memberId') || '';

    if (!this.memberId) {
      this.error = 'Member ID is required';
      return;
    }

    this.loadMemberExercises();
  }

  loadMemberExercises(): void {
    this.isLoading = true;
    this.error = '';

    this.fitnessPlanService.getMemberExerciseDetails(this.memberId).subscribe({
      next: (response: any) => {
        // console.log('Member exercise details response:', response);

        if (
          response &&
          response.memberExerciseDetails &&
          Array.isArray(response.memberExerciseDetails)
        ) {
          // Set member name from API response
          this.memberName = response.memberName || 'Member';

          // Transform the new API response to match our component's expected structure
          this.exercises = response.memberExerciseDetails.map(
            (exercise: any) => ({
              id: '', // Will be set when custom instructions are created
              memberId: this.memberId,
              exerciseId: exercise.exerciseId,
              exerciseName: exercise.exerciseName,
              customInstructions: '', // Default empty, will be populated if exists
              instructions: exercise.instructions,
              benefits: exercise.benefits,
              safetyTips: exercise.safetyTips,
              muscleGroup: exercise.muscleGroup,
              difficultyLevel: exercise.difficultyLevel,
              equipmentNeeded: exercise.equipmentNeeded,
              estimatedCaloriesBurned: exercise.estimatedCaloriesBurned,
              calorieUnit: exercise.calorieUnit,
              diabeticGuidance: exercise.diabeticGuidance,
              riskAssessment: exercise.riskAssessment,
            })
          );

          // Now load existing custom instructions for these exercises
          this.loadExistingInstructions();
        } else {
          this.exercises = [];
          this.filteredExercises = [...this.exercises];
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Error loading member exercise details:', error);
        this.error = 'Failed to load exercise details';
        this.isLoading = false;
      },
    });
  }

  private loadExistingInstructions(): void {
    // Load existing custom instructions for the member
    this.fitnessPlanService
      .getMemberExerciseInstructions(this.memberId)
      .subscribe({
        next: (instructionsResponse: any) => {
          // console.log('Existing instructions response:', instructionsResponse);

          if (
            instructionsResponse &&
            instructionsResponse.memberExerciseInstructions
          ) {
            // Merge existing custom instructions with exercise details
            instructionsResponse.memberExerciseInstructions.forEach(
              (instruction: any) => {
                const exerciseIndex = this.exercises.findIndex(
                  (ex) => ex.exerciseId === instruction.exerciseId
                );
                if (exerciseIndex !== -1) {
                  // Convert instructions array to string format for display
                  const customInstructionsString = instruction.instructions
                    ? instruction.instructions.join('\n')
                    : '';

                  this.exercises[exerciseIndex] = {
                    ...this.exercises[exerciseIndex],
                    id: instruction.id,
                    customInstructions: customInstructionsString,
                    createdAt: instruction.createdDate,
                    updatedAt: instruction.modifiedDate,
                    lastAssignedDate: instruction.lastAssignedDate,
                    totalAssignments: instruction.totalAssignments,
                  };
                }
              }
            );
          }

          this.filteredExercises = [...this.exercises];
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading existing instructions:', error);
          // Continue anyway with the exercise details we have
          this.filteredExercises = [...this.exercises];
          this.isLoading = false;
        },
      });
  }

  onSearchChange(): void {
    if (!this.searchTerm.trim()) {
      this.filteredExercises = [...this.exercises];
    } else {
      const search = this.searchTerm.toLowerCase();
      this.filteredExercises = this.exercises.filter((exercise) => {
        // Search in basic exercise info
        const basicMatch =
          exercise.exerciseName.toLowerCase().includes(search) ||
          exercise.customInstructions.toLowerCase().includes(search) ||
          exercise.muscleGroup?.toLowerCase().includes(search) ||
          exercise.difficultyLevel?.toLowerCase().includes(search);

        // Search in exercise details arrays
        const instructionsMatch = exercise.instructions?.some((inst) =>
          inst.toLowerCase().includes(search)
        );
        const benefitsMatch = exercise.benefits?.some((benefit) =>
          benefit.toLowerCase().includes(search)
        );
        const safetyMatch = exercise.safetyTips?.some((tip) =>
          tip.toLowerCase().includes(search)
        );
        const equipmentMatch = exercise.equipmentNeeded?.some((equip) =>
          equip.toLowerCase().includes(search)
        );

        // Search in guidance text
        const guidanceMatch = exercise.diabeticGuidance
          ?.toLowerCase()
          .includes(search);
        const riskMatch = exercise.riskAssessment
          ?.toLowerCase()
          .includes(search);

        return (
          basicMatch ||
          instructionsMatch ||
          benefitsMatch ||
          safetyMatch ||
          equipmentMatch ||
          guidanceMatch ||
          riskMatch
        );
      });
    }
  }

  editInstructions(exercise: ExerciseInstruction): void {
    const dialogRef = this.dialog.open(EditInstructionDialogComponent, {
      disableClose: false,
      hasBackdrop: true,
      backdropClass: 'custom-backdrop',
      data: {
        exercise: { ...exercise },
        memberName: this.memberName,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.saveInstructions(result);
      }
    });
  }

  saveInstructions(updatedExercise: ExerciseInstruction): void {
    this.isLoading = true;

    // Use the array format if available from the dialog, otherwise parse the string
    const instructionsArray =
      (updatedExercise as any).instructionsArray ||
      this.parseCustomInstructionsToArray(updatedExercise.customInstructions);

    const instructionData = {
      id: updatedExercise.id || null,
      memberId: this.memberId,
      exerciseId: updatedExercise.exerciseId,
      instructions: instructionsArray,
      createdBy: this.memberId, // Using memberId as createdBy for now
    };

    // console.log('Saving instruction data:', instructionData);

    this.fitnessPlanService
      .saveExerciseInstructions(instructionData)
      .subscribe({
        next: (response: any) => {
          // console.log('Instructions saved successfully:', response);

          // Update the local exercise data
          const index = this.exercises.findIndex(
            (ex) => ex.exerciseId === updatedExercise.exerciseId
          );
          if (index !== -1) {
            // Convert instructions array back to string format for display
            const customInstructionsString = instructionsArray.join('\n');

            this.exercises[index] = {
              ...this.exercises[index],
              id: response.id || response._id || updatedExercise.id, // Handle different ID field names
              customInstructions: customInstructionsString,
              createdAt:
                response.createdDate ||
                response.createdAt ||
                updatedExercise.createdAt,
              updatedAt:
                response.modifiedDate ||
                response.updatedAt ||
                new Date().toISOString(),
            };

            // console.log('Updated exercise:', this.exercises[index]);
          }

          // Update filtered exercises
          this.onSearchChange();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error saving instructions:', error);
          this.error = 'Failed to save instructions';
          this.isLoading = false;
        },
      });
  }

  deleteInstructions(exercise: ExerciseInstruction): void {
    if (!exercise.id) {
      console.error('Cannot delete instructions: No ID found');
      return;
    }

    // Open Material Design confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      disableClose: false,
      hasBackdrop: true,
      backdropClass: 'dark-backdrop',
      data: {
        title: 'Delete Custom Instructions',
        message: `Are you sure you want to delete custom instructions for "${exercise.exerciseName}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        icon: 'delete',
        confirmColor: 'warn',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;

        this.fitnessPlanService
          .deleteExerciseInstructionsById(exercise.id!)
          .subscribe({
            next: (response: any) => {
              // console.log('Instructions deleted successfully:', response);

              // Remove from local array
              this.exercises = this.exercises.filter(
                (ex) => ex.id !== exercise.id
              );
              this.onSearchChange();
              this.isLoading = false;
            },
            error: (error) => {
              console.error('Error deleting instructions:', error);
              this.error = 'Failed to delete instructions';
              this.isLoading = false;
            },
          });
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/fitness-plan']);
  }

  hasInstructions(exercise: ExerciseInstruction): boolean {
    return !!(
      exercise.customInstructions && exercise.customInstructions.trim()
    );
  }

  getInstructionsPreview(instructions: string): string {
    if (!instructions || !instructions.trim()) {
      return 'No custom instructions set';
    }

    // If it's a formatted list, show first few items
    const lines = instructions.split('\n').filter((line) => line.trim());
    if (lines.length > 1) {
      const preview = lines.slice(0, 2).join(' • ');
      return lines.length > 2 ? preview + '...' : preview;
    }

    // Single line or paragraph - truncate if too long
    return instructions.length > 100
      ? instructions.substring(0, 100) + '...'
      : instructions;
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  }

  formatEquipment(equipment: string): string {
    return equipment
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  formatCalorieUnit(unit: string | undefined): string {
    if (!unit) return 'per rep';
    return unit.replace(/_/g, ' ').toLowerCase();
  }

  isEquipmentNone(equipment: string[]): boolean {
    return (
      equipment &&
      equipment.length === 1 &&
      equipment[0].toLowerCase() === 'none'
    );
  }

  trackByExerciseId(index: number, exercise: ExerciseInstruction): string {
    return exercise.exerciseId;
  }

  toggleSection(sectionId: string): void {
    if (this.expandedSections.has(sectionId)) {
      this.expandedSections.delete(sectionId);
    } else {
      this.expandedSections.add(sectionId);
    }
  }

  isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections.has(sectionId);
  }

  formatCustomInstructions(instructions: string): string {
    if (!instructions || !instructions.trim()) {
      return '';
    }

    // Check if it's already formatted as a numbered list
    const lines = instructions.split('\n').filter((line) => line.trim());
    if (lines.length > 1 && lines.some((line) => /^\d+\./.test(line.trim()))) {
      // It's already a numbered list, return as is
      return instructions;
    }

    // If it's a single line or unformatted text, return as is
    return instructions;
  }

  parseCustomInstructionsToArray(customInstructions: string): string[] {
    if (!customInstructions || !customInstructions.trim()) {
      return [];
    }

    // Split by newlines and clean up numbered list format
    return customInstructions
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        // Remove numbering if present (e.g., "1. instruction" -> "instruction")
        return line.replace(/^\d+\.\s*/, '').trim();
      })
      .filter((instruction) => instruction.length > 0);
  }

  getInstructionCount(customInstructions: string): number {
    if (!customInstructions || !customInstructions.trim()) {
      return 0;
    }

    const instructionsArray =
      this.parseCustomInstructionsToArray(customInstructions);
    return instructionsArray.length;
  }
}
